import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { certificatesApi } from '../api/certificatesApi';
import type { CertificateFilters, CertificateFormValues } from '../types/certificate';

export function useCertificatesQuery(
  filters: CertificateFilters,
  page: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['gateway', 'certificates', filters, page],
    queryFn: () => certificatesApi.list({ ...filters, page, size: 20, sort: 'createdAt,desc' }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useCertificateQuery(id: string | null) {
  return useQuery({
    queryKey: ['gateway', 'certificate', id],
    queryFn: () => certificatesApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateCertificate(onSuccess?: (cn: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CertificateFormValues) => certificatesApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'certificates'] });
      onSuccess?.(created.cn);
    },
  });
}

export function useReplaceCertificate(id: string, onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CertificateFormValues) => certificatesApi.replace(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'certificates'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'certificate', id] });
      onSuccess?.();
    },
  });
}

export function useDeleteCertificate(onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => certificatesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'certificates'] });
      onSuccess?.();
    },
  });
}
